/**
 * Email Orchestrator Service
 *
 * Enterprise-grade email service orchestration layer.
 * Routes email operations to the appropriate provider service (Gmail, Outlook, etc.)
 * All OAuth tokens are managed server-side; this layer only triggers backend flows.
 */

import {
  initiateAuth as initiateGmailAuth,
  handleCallback as handleGmailCallback,
  fetchMessages as fetchGmailMessages,
  sendMessage as sendGmailMessage,
  deleteMessage as deleteGmailMessage,
  isSupportedProvider as isGmailProvider,
} from './gmailService';
import {
  initiateAuth as initiateOutlookAuth,
  handleCallback as handleOutlookCallback,
  fetchMessages as fetchOutlookMessages,
  sendMessage as sendOutlookMessage,
  deleteMessage as deleteOutlookMessage,
  isSupportedProvider as isOutlookProvider,
} from './outlookService';
import type { EmailMessage, GetMessagesOptions, SendMessageOptions, EmailProvider } from './types';

/**
 * Initiate OAuth flow for the specified email provider.
 * Redirects user to provider's consent screen.
 */
export async function initiateAuth(provider: EmailProvider): Promise<void> {
  console.log(`📧 Orchestrator: Initiating ${provider} OAuth...`);

  if (isGmailProvider(provider)) {
    return initiateGmailAuth();
  }

  if (isOutlookProvider(provider)) {
    return initiateOutlookAuth();
  }

  throw new Error(`OAuth not yet implemented for provider: ${provider}`);
}

/**
 * Handle OAuth callback after user consents.
 * Exchanges authorization code for tokens and persists account server-side.
 *
 * @param provider - The email provider (gmail, outlook, etc.)
 * @param code - The authorization code from OAuth redirect
 * @returns true if account was successfully connected, false otherwise
 */
export async function handleCallback(provider: EmailProvider, code: string): Promise<boolean> {
  console.log(`📧 Orchestrator: Handling ${provider} callback...`);

  if (isGmailProvider(provider)) {
    return handleGmailCallback(code);
  }

  if (isOutlookProvider(provider)) {
    return handleOutlookCallback(code);
  }

  throw new Error(`OAuth callback not yet implemented for provider: ${provider}`);
}

/**
 * Fetch email messages from the specified provider.
 * Backend retrieves encrypted tokens and calls provider API.
 */
export async function fetchMessages(
  provider: EmailProvider,
  options: GetMessagesOptions = {}
): Promise<EmailMessage[]> {
  console.log(`📧 Orchestrator: Fetching messages from ${provider}...`);

  if (isGmailProvider(provider)) {
    return fetchGmailMessages(options);
  }

  if (isOutlookProvider(provider)) {
    return fetchOutlookMessages(options);
  }

  throw new Error(`Message fetch not yet implemented for provider: ${provider}`);
}

/**
 * Send an email message via the specified provider.
 */
export async function sendEmail(
  provider: EmailProvider,
  message: SendMessageOptions
): Promise<void> {
  console.log(`📧 Orchestrator: Sending email via ${provider}...`);

  if (isGmailProvider(provider)) {
    return sendGmailMessage(message);
  }

  if (isOutlookProvider(provider)) {
    return sendOutlookMessage(message);
  }

  throw new Error(`Send not yet implemented for provider: ${provider}`);
}

/**
 * Delete an email message from the specified provider.
 */
export async function deleteEmail(provider: EmailProvider, messageId: string): Promise<void> {
  console.log(`📧 Orchestrator: Deleting message ${messageId} from ${provider}...`);

  if (isGmailProvider(provider)) {
    return deleteGmailMessage(messageId);
  }

  if (isOutlookProvider(provider)) {
    return deleteOutlookMessage(messageId);
  }

  throw new Error(`Delete not yet implemented for provider: ${provider}`);
}

/**
 * Check if a provider is supported by the orchestrator.
 */
export function isSupportedProvider(provider: EmailProvider): boolean {
  return isGmailProvider(provider) || isOutlookProvider(provider);
}
